import React from "react";
import DocsCallout from "../../../components/dashboard/common/Documentation/DocsCallout";
import DocsPageHeader from "../../../components/dashboard/common/Documentation/DocsPageHeader";
import SearchHighlighter from "../../../components/dashboard/common/Documentation/SearchHighlighter";

const AssetInventory = () => {
  return (
    <SearchHighlighter>
      <DocsPageHeader 
        number="1" 
        title="Asset Inventory & Attack Surface" 
        description="The Asset Inventory is your central registry for all digital systems. Every asset requires precise identity, ownership, and security context before it can be enrolled in a testing program."
      />


      {/* 1.1 Identity & Ownership */}
      <h2 id="identity-ownership" className="text-2xl font-bold text-black mt-16 mb-4">1.1 Identity & Ownership</h2>
      <p className="text-slate-600 mb-8">Establish the primary identifiers and accountability for the asset.</p>
      
      <div className="overflow-x-auto border border-[#eaeaea] rounded-xl mb-8">
        <table className="min-w-full divide-y divide-[#eaeaea] m-0">
          <thead className="bg-[#fafafa]">
            <tr>
              <th className="px-6 py-4 text-left text-xs font-bold text-black uppercase tracking-wider border-r border-[#eaeaea]">Field</th>
              <th className="px-6 py-4 text-left text-xs font-bold text-black uppercase tracking-wider">Description & Guidance</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-[#eaeaea]">
            <tr>
              <td className="px-6 py-4 text-sm font-bold text-black border-r border-[#eaeaea]">Asset Name</td>
              <td className="px-6 py-4 text-sm text-slate-600">Primary identifier (3–50 chars). Use the name used internally for the product.</td>
            </tr>
            <tr>
              <td className="px-6 py-4 text-sm font-bold text-black border-r border-[#eaeaea]">Asset Summary</td>
              <td className="px-6 py-4 text-sm text-slate-600">Non-technical description of the asset's purpose and business significance.</td>
            </tr>
            <tr>
              <td className="px-6 py-4 text-sm font-bold text-black border-r border-[#eaeaea]">Risk Owner</td>
              <td className="px-6 py-4 text-sm text-slate-600">Person accountable for risk acceptance. Must be a registered team member.</td>
            </tr>
            <tr>
              <td className="px-6 py-4 text-sm font-bold text-black border-r border-[#eaeaea]">Technical Owner</td>
              <td className="px-6 py-4 text-sm text-slate-600">Person or team responsible for remediation. Can be set to "Invite Later".</td>
            </tr>
          </tbody>
        </table>
      </div>

      {/* 1.2 Lifecycle & Usage */}
      <h2 id="lifecycle-usage" className="text-2xl font-bold text-black mt-16 mb-4">1.2 Lifecycle & Usage</h2>
      <p className="text-slate-600 mb-8">Define where the product stands in its development cycle and how it is deployed.</p>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
        <div className="p-6 border border-[#eaeaea] rounded-xl bg-[#fafafa]">
          <h3 className="font-bold text-black mb-3 text-sm uppercase tracking-wider">Lifecycle Status</h3>
          <ul className="space-y-2 text-sm text-slate-600 list-none p-0 m-0">
             <li><b className="text-black">Active:</b> Currently in production.</li>
             <li><b className="text-black">Under Development:</b> Staging, UAT, or Dev environments.</li>
             <li><b className="text-black">Maintenance:</b> Stable with minimal updates.</li>
             <li><b className="text-black">Deprecated/Retired:</b> Phasing out or no longer in use.</li>
          </ul>
        </div>
        <div className="p-6 border border-[#eaeaea] rounded-xl bg-[#fafafa]">
          <h3 className="font-bold text-black mb-3 text-sm uppercase tracking-wider">Change Frequency</h3>
          <ul className="space-y-2 text-sm text-slate-600 list-none p-0 m-0">
             <li><b className="text-black">Rarely:</b> Few times a year.</li>
             <li><b className="text-black">Occasionally:</b> Quarterly updates.</li>
             <li><b className="text-black">Continuously:</b> Weekly or CI/CD driven changes.</li>
          </ul>
        </div>
      </div>

      <div className="p-6 border border-[#eaeaea] rounded-xl mb-8">
        <h3 className="font-bold text-black mb-4">Deployment & Audience</h3>
        <p className="text-sm text-slate-600 mb-4">
          <b className="text-black">Deployment Model:</b> SaaS (Vendor-managed), Customer-hosted (On-premise), Hybrid, or Internal-only (Company infrastructure).
        </p>
        <p className="text-sm text-slate-600 m-0">
          <b className="text-black">Primary Audience:</b> External customers, Internal employees, Partners/vendors, or Machine-to-machine (Systems only).
        </p>
      </div>

      {/* 1.3 Classification & Criticality */}
      <h2 id="classification-criticality" className="text-2xl font-bold text-black mt-16 mb-4">1.3 Classification & Criticality</h2>
      <p className="text-slate-600 mb-8">Categorize the technical stack and determine the business impact of a breach.</p>

      <div className="p-6 border border-[#eaeaea] rounded-xl bg-[#fafafa] mb-10">
        <h3 className="font-bold text-black mb-4 text-sm uppercase tracking-wider">Asset Categories</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-y-4 gap-x-8">
          <div className="text-sm">
            <b className="text-black block mb-1">Digital Applications</b>
            <span className="text-slate-500">Web, API, Mobile (Android/iOS)</span>
          </div>
          <div className="text-sm">
            <b className="text-black block mb-1">Infrastructure</b>
            <span className="text-slate-500">Cloud, External/Internal Servers</span>
          </div>
          <div className="text-sm">
            <b className="text-black block mb-1">Identity & Data</b>
            <span className="text-slate-500">IDPs, Directories, Datasets</span>
          </div>
          <div className="text-sm">
            <b className="text-black block mb-1">Code & Supply Chain</b>
            <span className="text-slate-500">Source code, CI/CD, Dependencies</span>
          </div>
          <div className="text-sm">
            <b className="text-black block mb-1">Integrations & Devices</b>
            <span className="text-slate-500">Webhooks, IoT, External APIs</span>
          </div>
        </div>
      </div>

      <div className="overflow-x-auto border border-[#eaeaea] rounded-xl mb-8">
        <table className="min-w-full divide-y divide-[#eaeaea] m-0">
          <thead className="bg-[#fafafa]">
            <tr>
              <th className="px-6 py-4 text-left text-xs font-bold text-black uppercase tracking-wider border-r border-[#eaeaea]">Criticality Level</th>
              <th className="px-6 py-4 text-left text-xs font-bold text-black uppercase tracking-wider">Business Impact Definition</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-[#eaeaea]">
            <tr>
              <td className="px-6 py-4 text-sm font-bold text-red-600 border-r border-[#eaeaea]">Critical</td>
              <td className="px-6 py-4 text-sm text-slate-600">Business cannot function or faces serious damage/regulatory risk.</td>
            </tr>
            <tr>
              <td className="px-6 py-4 text-sm font-bold text-orange-600 border-r border-[#eaeaea]">High</td>
              <td className="px-6 py-4 text-sm text-slate-600">Business is significantly impacted; core operations are disrupted.</td>
            </tr>
            <tr>
              <td className="px-6 py-4 text-sm font-bold text-amber-600 border-r border-[#eaeaea]">Medium</td>
              <td className="px-6 py-4 text-sm text-slate-600">Important but tolerable disruption. Backup paths likely exist.</td>
            </tr>
            <tr>
              <td className="px-6 py-4 text-sm font-bold text-emerald-600 border-r border-[#eaeaea]">Low</td>
              <td className="px-6 py-4 text-sm text-slate-600">Nice-to-have system. Failure causes minimal operational pain.</td>
            </tr>
          </tbody>
        </table>
      </div>

      <DocsCallout type="tip">
        <b>Compliance Frameworks:</b> You can optionally select frameworks like SOC 2, ISO 27001, PCI-DSS, or HIPAA to align testing with regulatory requirements.
      </DocsCallout>

      {/* 1.4 Data & Privacy */}
      <h2 id="data-privacy" className="text-2xl font-bold text-black mt-16 mb-4">1.4 Data & Privacy</h2>
      <p className="text-slate-600 mb-8">Specify the sensitivity of the data handled and its residency requirements.</p>

      <div className="space-y-6">
        <div className="p-6 border border-[#eaeaea] rounded-xl">
          <h3 className="font-bold text-black mb-3">Data Classification</h3>
          <p className="text-sm text-slate-600 mb-0">
            <b className="text-black">Highly Confidential:</b> PII, Financials, or Intellectual Property.<br/>
            <b className="text-black">Regulated:</b> Protected by law (GDPR, HIPAA, PCI).<br/>
            <b className="text-black">Confidential:</b> Customer or business-sensitive data.<br/>
            <b className="text-black">Internal / Public:</b> Restricted to employees or open to all.
          </p>
        </div>
        <div className="p-6 border border-[#eaeaea] rounded-xl bg-[#fafafa]">
          <h3 className="font-bold text-black mb-2">Data Residency</h3>
          <p className="text-sm text-slate-600 m-0">
            Select specific countries or regions (or "Global") where data is stored. This influences researcher pod assignment to comply with data sovereignty.
          </p>
        </div>
      </div>

      {/* 1.5 Access Control */}
      <h2 id="access-control" className="text-2xl font-bold text-black mt-16 mb-4">1.5 Access Control</h2>
      <p className="text-slate-600 mb-8">Define how users and researchers interact with the product's security gates.</p>

      <div className="overflow-x-auto border border-[#eaeaea] rounded-xl mb-8">
        <table className="min-w-full divide-y divide-[#eaeaea] m-0">
          <thead className="bg-[#fafafa]">
            <tr>
              <th className="px-6 py-4 text-left text-xs font-bold text-black uppercase tracking-wider border-r border-[#eaeaea]">Feature</th>
              <th className="px-6 py-4 text-left text-xs font-bold text-black uppercase tracking-wider">Details</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-[#eaeaea]">
            <tr>
              <td className="px-6 py-4 text-sm font-bold text-black border-r border-[#eaeaea]">Authentication</td>
              <td className="px-6 py-4 text-sm text-slate-600">None (Public), Partial (some pages), or Required (full login).</td>
            </tr>
            <tr>
              <td className="px-6 py-4 text-sm font-bold text-black border-r border-[#eaeaea]">Authorization Model</td>
              <td className="px-6 py-4 text-sm text-slate-600">RBAC (Roles), ABAC (Attributes), Custom, or Unknown.</td>
            </tr>
            <tr>
              <td className="px-6 py-4 text-sm font-bold text-black border-r border-[#eaeaea]">Identity Provider</td>
              <td className="px-6 py-4 text-sm text-slate-600">e.g., Azure AD, Okta, Auth0, Google SSO, or Custom login.</td>
            </tr>
            <tr>
              <td className="px-6 py-4 text-sm font-bold text-black border-r border-[#eaeaea]">Typical User Roles</td>
              <td className="px-6 py-4 text-sm text-slate-600">List common roles (Admin, Editor, Guest) for privilege testing.</td>
            </tr>
          </tbody>
        </table>
      </div>

      {/* 1.6 Security Testing Focus */}
      <h2 id="security-testing" className="text-2xl font-bold text-black mt-16 mb-4">1.6 Security Testing Focus</h2>
      <p className="text-slate-600 mb-8">Define the technical exposure and the simulation focus for researchers.</p>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
        <div className="p-6 border border-[#eaeaea] rounded-xl">
          <h3 className="font-bold text-black mb-3 text-sm uppercase tracking-wider">Internet Exposure</h3>
          <ul className="space-y-2 text-sm text-slate-600 list-none p-0 m-0">
             <li><b className="text-black">Public:</b> Open internet, no login.</li>
             <li><b className="text-black">Restricted:</b> Internet + login required.</li>
             <li><b className="text-black">Internal:</b> Private network / VPN only.</li>
          </ul>
        </div>
        <div className="p-6 border border-[#eaeaea] rounded-xl bg-[#fafafa]">
          <h3 className="font-bold text-black mb-3 text-sm uppercase tracking-wider">Primary Focus Units</h3>
          <div className="flex flex-wrap gap-2 text-xs font-bold">
            <span className="px-3 py-1 bg-white border border-[#eaeaea] rounded-full">WEB</span>
            <span className="px-3 py-1 bg-white border border-[#eaeaea] rounded-full">API</span>
            <span className="px-3 py-1 bg-white border border-[#eaeaea] rounded-full">MOBILE</span>
            <span className="px-3 py-1 bg-white border border-[#eaeaea] rounded-full">NETWORK</span>
            <span className="px-3 py-1 bg-white border border-[#eaeaea] rounded-full">CLOUD</span>
          </div>
        </div>
      </div>

      <DocsCallout type="warning">
        <b>Rules of Engagement:</b> Always specify "Testing Constraints" (e.g., "Do not delete records") to ensure non-disruptive testing.
      </DocsCallout>

      {/* 1.7 Governance & Attestation */}
      <h2 id="governance-attestation" className="text-2xl font-bold text-black mt-16 mb-4">1.7 Governance & Attestation</h2>
      <p className="text-slate-600 mb-8">Finalize the registry with a formal legal handshake.</p>

      <div className="p-8 border-2 border-slate-900 rounded-2xl bg-slate-50">
        <h3 className="text-xl font-bold text-black mb-4">Legal Rights & Authorization</h3>
        <p className="text-sm text-slate-600 leading-relaxed mb-6">
          Before an asset is activated, an authorized team member must confirm that the organization holds explicit testing rights. This attestation is digitally signed with a username and timestamp, forming a permanent audit record for compliance standards like SOC2.
        </p>
        <div className="flex items-center gap-6 pt-6 border-t border-slate-200">
           <div className="text-[10px] font-black uppercase tracking-widest text-slate-400">Signed By: [Current User]</div>
           <div className="text-[10px] font-black uppercase tracking-widest text-slate-400">Date: [UTC Timestamp]</div>
        </div>
      </div>
    </SearchHighlighter>
  );
};

export default AssetInventory;
